import { graphql, useStaticQuery } from 'gatsby'

import useLocale from './useLocale'

/**
 * Hook to fetch all partners data from Contentful
 * Used internally by the public hooks
 */
function useAllPartnersData() {
  const data = useStaticQuery(graphql`
    query PartnersQuery {
      allContentfulBlockPartners {
        edges {
          node {
            title
            node_locale
            partners {
              name
              type
              link
              page {
                slug
              }
            }
          }
        }
      }
    }
  `)

  return data.allContentfulBlockPartners.edges
}

/**
 * Custom hook to fetch partners data from Contentful
 * Specifically looks for the "Selected technologies"/"Utvalda teknologier" block
 * Falls back to English if no block is found for the current locale
 *
 * @returns {Object} An object containing the partners block title and partners array
 */
export function usePartnersData() {
  const locale = useLocale()
  const allEdges = useAllPartnersData()

  // Define the title we're looking for in each language (most reliable approach)
  const targetTitles = {
    en: 'Selected technologies',
    sv: 'Utvalda teknologier',
    da: 'Udvalgte teknologier',
    de: 'Ausgewählte Technologien',
    nl: 'Geselecteerde technologieën',
  }

  // Get the target title for the current locale
  const targetTitle =
    targetTitles[locale as keyof typeof targetTitles] || targetTitles.en

  // First try exact match in current locale
  const currentLocaleEdge = allEdges.find(
    (edge: any) =>
      edge.node?.node_locale === locale && edge.node?.title === targetTitle
  )

  if (currentLocaleEdge && currentLocaleEdge.node) {
    return {
      title: currentLocaleEdge.node.title || targetTitle,
      partners: currentLocaleEdge.node.partners || [],
    }
  }

  // Next try English with exact match
  if (locale !== 'en') {
    const englishEdge = allEdges.find(
      (edge: any) =>
        edge.node?.node_locale === 'en' && edge.node?.title === targetTitles.en
    )

    if (englishEdge && englishEdge.node) {
      return {
        title: englishEdge.node.title || targetTitles.en,
        partners: englishEdge.node.partners || [],
      }
    }
  }

  // If no exact match, try to find any partners block in current locale
  const anyLocaleEdge = allEdges.find(
    (edge: any) =>
      edge.node?.node_locale === locale &&
      Array.isArray(edge.node?.partners) &&
      edge.node.partners.length > 0
  )

  if (anyLocaleEdge && anyLocaleEdge.node) {
    return {
      // Use our desired title instead of the block's actual title
      title: targetTitle,
      partners: anyLocaleEdge.node.partners || [],
    }
  }

  // Last resort - any partners block in English
  const anyEnglishEdge = allEdges.find(
    (edge: any) =>
      edge.node?.node_locale === 'en' &&
      Array.isArray(edge.node?.partners) &&
      edge.node.partners.length > 0
  )

  if (anyEnglishEdge && anyEnglishEdge.node) {
    return {
      title: targetTitles.en,
      partners: anyEnglishEdge.node.partners || [],
    }
  }

  // No suitable blocks found at all
  return {
    title: targetTitle,
    partners: [],
  }
}
